import React, { useContext, useEffect, useState } from "react";
import "./Collaborators.css";
import { TbSend } from "react-icons/tb";
import { useLanguage } from "../../LanguageProvider";
import InputField from "../../components/InputField/InputField";
import Chip from "../../components/Chip/Chip";
import { GeneralContext } from "../../GeneralProvider";
import ModuleSidebar from "../../components/ModuleSidebar/ModuleSidebar";
import { GoPeople } from "react-icons/go";
import CollaboratorsTable from "./components/CollaboratorsTable/CollaboratorsTable";
import { useToaster } from "../../ToasterProvider";
import AlertBox from "../../components/AlertBox/AlertBox";

export default function Collaborators() {
  const { toastWarning, toastSuccess, toastError } = useToaster();
  const { getTranslation } = useLanguage();
  const {
    handleInviteCollaborators,
    handleGetReceivedInvitations,
    receivedInvitations,
    handleGetSentInvitations,
    sentInvitations,
    handleAcceptInvitation,
    handleRejectInvitation,
    collaborators,
    handleCancelCollaboratorInvitation,
    handleGetCollaborators,
    handleRemoveCollaborator,
    userId,
    receivedProjectInvitations,
    handleGetRecievedProjectInvitations,
    handleAcceptProjectInvitation,
    handleRejectProjectInvitation,
    handleResendCollaboratorInvitation,
  } = useContext(GeneralContext);
  const [inputValue, setInputValue] = useState("");
  const [addedCollaborators, setAddedCollaborators] = useState([]);
  const [userName, setUserName] = useState("");
  const [selectedSubmenu, setSelectedSubmenu] = useState(1);
  const [selectedCollaboratorToRemove, setSelectedCollaboratorToRemove] =
    useState("");
  const [removeCollaboratorModalOpen, setRemoveCollaboratorModalOpen] =
    useState(false);

  const handleOpenRemoveCollaboratorModal = (memberID) => {
    setSelectedCollaboratorToRemove(memberID);
    setRemoveCollaboratorModalOpen(true);
  };

  const handleCloseRemoveCollaboratorModal = () => {
    setSelectedCollaboratorToRemove("");
    setRemoveCollaboratorModalOpen(false);
  };

  const handleRemoveCollaboratorFromList = async () => {
    try {
      toastSuccess(getTranslation("successMessage"));
      handleRemoveCollaborator(selectedCollaboratorToRemove);
      setRemoveCollaboratorModalOpen(false);
    } catch (error) {
      console.error("Cancel invitation failed:", error);
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleCancelInvitation = async (invitationCode) => {
    try {
      handleCancelCollaboratorInvitation(invitationCode);
      toastSuccess(getTranslation("successMessage"));
    } catch (error) {
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const handleResendInvitation = async (invitationCode) => {
    try {
      handleResendCollaboratorInvitation(invitationCode);
      toastSuccess(getTranslation("successMessage"));
    } catch (error) {
      toastError(getTranslation("somethingWentWrong"));
    }
  };

  const sentInvitationsHeaders = [
    getTranslation("email"),
    getTranslation("expirationDate"),
    getTranslation("status"),
    getTranslation("actions"),
  ];

  const sentInvitationsData = sentInvitations?.map((invitation) => {
    const expirationDate = invitation.expiredDateTime
      ? new Date(invitation.expiredDateTime * 1000).toLocaleDateString()
      : getTranslation("noExpiry");

    // Eğer davetiye "Sent" ise, status sütunu "Sent" olarak kalırken, actions sütununda butonlar yer alsın.
    if (invitation.status === "3") {
      return {
        email: invitation.invitedEmail,
        expirationDate,
        status: getTranslation("sent"),
        actions: (
          <div className="collaborators-table-status-holder">
            <button
              onClick={() => handleCancelInvitation(invitation.invitationCode)}
              className="collaborators-table-reject-button"
            >
              {getTranslation("cancel")}
            </button>

            <button
              onClick={() => handleResendInvitation(invitation.invitationCode)}
              className="collaborators-table-resend-button"
            >
              {getTranslation("resend")}
            </button>
          </div>
        ),
      };
    } else {
      // Örneğin davetiye Accepted ise
      return {
        email: invitation.invitedEmail,
        expirationDate,
        status: getTranslation("accepted"),
        actions: "", // Butonlar gösterilmiyor.
      };
    }
  });

  const receivedInvitationsHeaders = [
    getTranslation("name") + " " + getTranslation("lastname"),
    getTranslation("email"),
    getTranslation("expirationDate"),
    getTranslation("actions"),
  ];

  const receivedProjectInvitationsHeaders = [
    getTranslation("name") + " " + getTranslation("lastname"),
    getTranslation("email"),
    getTranslation("projectTitle"),
    getTranslation("actions"),
  ];

  const collaboratorsHeaders = [
    getTranslation("name") + " " + getTranslation("lastname"),
    getTranslation("email"),
    getTranslation("actions"),
  ];

  const receivedInvitationsData = receivedInvitations?.map((invitation) => ({
    nameLastname: `${invitation.inviterName} ${invitation.inviterLastname}`,
    email: invitation.inviterEmail,
    expirationDate: invitation.expiredDateTime
      ? new Date(invitation.expiredDateTime * 1000).toLocaleDateString()
      : "No Expiry",
    status: (
      <div className="collaborators-table-status-holder">
        <button
          onClick={() => handleAcceptInvitation(invitation.invitationCode)}
          className="collaborators-table-accept-button"
        >
          {getTranslation("accept")}
        </button>
        <button
          onClick={() => handleRejectInvitation(invitation.invitationCode)}
          className="collaborators-table-reject-button"
        >
          {getTranslation("reject")}
        </button>
      </div>
    ),
  }));

  const receivedProjectInvitationsData = receivedProjectInvitations?.map(
    (invitation) => ({
      nameLastname: `${invitation.name} ${invitation.lastname}`,
      email: invitation.email,
      projectTitle: invitation.projectTitle,
      actions: (
        <div className="collaborators-table-status-holder">
          <button
            onClick={() => handleAcceptProjectInvitation(invitation.authID)}
            className="collaborators-table-accept-button"
          >
            {getTranslation("accept")}
          </button>
          <button
            onClick={() => handleRejectProjectInvitation(invitation.authID)}
            className="collaborators-table-reject-button"
          >
            {getTranslation("reject")}
          </button>
        </div>
      ),
    })
  );

  const collaboratorsData = collaborators
    ?.filter((x) => x.memberID !== userId)
    .map((collaborator) => ({
      nameLastname: `${collaborator.name} ${collaborator.lastname}`,
      email: collaborator.email,
      actions: (
        <div className="collaborators-table-status-holder">
          <button
            onClick={() =>
              handleOpenRemoveCollaboratorModal(collaborator.memberID)
            }
            className="collaborators-table-reject-button"
          >
            {getTranslation("removeCollaborator")}
          </button>
        </div>
      ),
    }));

  // Header ile data key eşleştirmesi
  const sentInvitationsDataFieldMapping = {
    [getTranslation("email")]: "email",
    [getTranslation("expirationDate")]: "expirationDate",
    [getTranslation("status")]: "status",
    [getTranslation("actions")]: "actions", // Mapping yeni sütun için
  };

  const receivedInvitationsDataFieldMapping = {
    [getTranslation("name") + " " + getTranslation("lastname")]: "nameLastname",
    [getTranslation("email")]: "email",
    [getTranslation("expirationDate")]: "expirationDate",
    [getTranslation("actions")]: "status",
  };

  const receivedProjectInvitationsDataFieldMapping = {
    [getTranslation("name") + " " + getTranslation("lastname")]: "nameLastname",
    [getTranslation("email")]: "email",
    [getTranslation("projectTitle")]: "projectTitle",
    [getTranslation("actions")]: "actions",
  };

  const collaboratorsDataFieldMapping = {
    [getTranslation("name") + " " + getTranslation("lastname")]: "nameLastname",
    [getTranslation("email")]: "email",
    [getTranslation("actions")]: "actions",
  };

  const menus = [
    {
      sections: [
        {
          title: getTranslation("collaborators"),
          icon: <GoPeople size={20} />,
          submenu: [
            { title: getTranslation("sentInvitations"), id: 1 },
            { title: getTranslation("receivedInvitations"), id: 2 },
            { title: getTranslation("myCollaborators"), id: 3 },
          ],
        },
        {
          title: getTranslation("projects"),
          icon: <GoPeople size={20} />,
          submenu: [{ title: getTranslation("receivedInvitations"), id: 4 }],
        },
      ],
    },
  ];

  const handleSubmenuClick = (submenu) => {
    setSelectedSubmenu(submenu.id);
    if (submenu.id === 1) {
      handleGetSentInvitations();
    } else if (submenu.id === 2) {
      handleGetReceivedInvitations();
    } else if (submenu.id === 3) {
      handleGetCollaborators();
    } else if (submenu.id === 4) {
      handleGetRecievedProjectInvitations();
    }
  };

  const handleAddCollaborator = () => {
    if (inputValue.trim() === "") return;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inputValue.trim())) {
      toastWarning(getTranslation("emailError"));
      return;
    }
    if (!addedCollaborators.includes(inputValue.trim())) {
      setAddedCollaborators([...addedCollaborators, inputValue.trim()]);
      setInputValue("");
    }
  };

  const handleRemoveCollaboratorChip = (email) => {
    setAddedCollaborators(
      addedCollaborators.filter((collaborator) => collaborator !== email)
    );
  };

  const handleSendInvitation = async () => {
    if (addedCollaborators?.length > 0) {
      try {
        const result = await handleInviteCollaborators(addedCollaborators);
        if (result.success) {
          setAddedCollaborators([]);
          toastSuccess(getTranslation("allInvitesSentSuccessfully"));
        } else if (
          result.specialMessage === "You can not send an invitation to yourself"
        ) {
          toastError(getTranslation("youCanNotSendAnInvitationToYourself"));
        } else {
          toastError(getTranslation("someInvitesCouldntBeSent"));
        }
      } catch (error) {
        console.error("Invitation process failed:", error);
        toastError(getTranslation("somethingWentWrong"));
      }
    } else {
      toastWarning(getTranslation("invitationListIsEmpty"));
    }
  };

  useEffect(() => {
    const sessionUser = JSON.parse(sessionStorage.getItem("user"));
    const localUser = JSON.parse(localStorage.getItem("user"));

    if (sessionUser) {
      setUserName(`${sessionUser.name} ${sessionUser.lastname}`);
    } else if (localUser) {
      setUserName(`${localUser.name} ${localUser.lastname}`);
    } else {
      setUserName("User");
    }

    handleGetReceivedInvitations();
    handleGetSentInvitations();
    handleGetRecievedProjectInvitations();
  }, []);

  return (
    <div className="collaborators-container">
      <ModuleSidebar
        title={userName}
        menus={menus}
        selectedSubmenu={selectedSubmenu}
        onSubmenuClick={handleSubmenuClick}
      />

      <div className="collaborators-content">
        <div className="collaborators-inner-content">
          {selectedSubmenu === 1 && (
            <>
              <p className="collaborators-title">
                {getTranslation("inviteCollaborators")}
              </p>

              <div className="collaborators-input-holder">
                <InputField
                  placeholder={getTranslation("collaboratorsEmailAddress")}
                  type="email"
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault(); // Prevent form submission or other default actions
                      handleAddCollaborator();
                    }
                  }}
                  value={inputValue}
                  style={{ height: "42px" }}
                />
                <button
                  className="collaborators-add-button"
                  onClick={handleAddCollaborator}
                >
                  {getTranslation("add")}
                </button>
              </div>
              <div className="collaborators-chips">
                {addedCollaborators.map((collaborator, index) => (
                  <Chip
                    onDeleteClicked={handleRemoveCollaboratorChip}
                    label={collaborator}
                    key={index}
                  />
                ))}
              </div>

              {addedCollaborators.length > 0 && (
                <div
                  style={{
                    width: "100%",
                    flexDirection: "row",
                    display: "flex",
                    justifyContent: "flex-end",
                  }}
                >
                  <button
                    onClick={handleSendInvitation}
                    className="collaborators-send-invite-button"
                  >
                    <TbSend strokeWidth={1} />{" "}
                    {getTranslation("sendInvitation")}
                  </button>
                </div>
              )}

              <p className="collaborators-title">
                {getTranslation("inviteList")}
              </p>
              <CollaboratorsTable
                headers={sentInvitationsHeaders}
                data={sentInvitationsData}
                fieldMapping={sentInvitationsDataFieldMapping}
              />
            </>
          )}

          {selectedSubmenu === 2 && (
            <>
              <p className="collaborators-title">
                {getTranslation("inviteList")}
              </p>
              <CollaboratorsTable
                headers={receivedInvitationsHeaders}
                data={receivedInvitationsData}
                fieldMapping={receivedInvitationsDataFieldMapping}
              />
            </>
          )}

          {selectedSubmenu === 3 && (
            <>
              <p className="collaborators-title">
                {getTranslation("myCollaborators")}
              </p>
              <CollaboratorsTable
                headers={collaboratorsHeaders}
                data={collaboratorsData}
                fieldMapping={collaboratorsDataFieldMapping}
              />
            </>
          )}

          {selectedSubmenu === 4 && (
            <>
              <p className="collaborators-title">
                {getTranslation("inviteList")}
              </p>
              <CollaboratorsTable
                headers={receivedProjectInvitationsHeaders}
                data={receivedProjectInvitationsData}
                fieldMapping={receivedProjectInvitationsDataFieldMapping}
              />
            </>
          )}
        </div>
      </div>

      <AlertBox
        isOpen={removeCollaboratorModalOpen}
        title={getTranslation("areYouSure")}
        message={getTranslation("removeCollaboratorConfirmation")}
        onConfirm={handleRemoveCollaboratorFromList}
        onCancel={handleCloseRemoveCollaboratorModal}
        confirmText={getTranslation("yes")}
        cancelText={getTranslation("no")}
        type={"warning"}
      />
    </div>
  );
}
